import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Dialog, CircularProgress } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import type { IEquipmentTypeSlim } from "App/Modules/Models";
import type { IEquipmentTypeRepository, IManufacturersRepository } from "App/Modules/Repositories";
import type { IPageService } from "App/Modules/Services";

import { SearchField } from "App/ReactComponents/Shared";

import "./_equipment-types.scss";
import { useEquipmentTypes } from "./useEquipmentTypes";
import { AddEquipmentType } from "./AddEquipmentType";

type EquipmentTypesProps = {
    mmEquipmentTypeRepository: IEquipmentTypeRepository;
    irManufacturersRepository: IManufacturersRepository;
    irPageService: IPageService;
};

export const EquipmentTypes = (props: EquipmentTypesProps) => {
    const { t } = useTranslation();
    const [globalFilter, setGlobalFilter] = useState("");
    const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
    const { equipmentTypes, addEquipmentType, loadingEquipmentTypes } = useEquipmentTypes(
        props.mmEquipmentTypeRepository,
        props.irPageService,
    );

    const filterFunc = (eq: IEquipmentTypeSlim) => {
        if (!globalFilter) {
            return true;
        }
        return (
            eq.displayName.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1 ||
            eq.className.toLowerCase().indexOf(globalFilter.toLowerCase()) > -1
        );
    };

    return (
        <div className="equipment-types card">
            <h1 className="title title--h2">{t("BeforeLogin.EquipmentType.EquipmentTypes")}</h1>
            <div className="equipment-types__functions">
                <div className="equipment-types__filter">
                    <SearchField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                </div>
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} fixedWidth />}
                    onClick={() => {
                        setShowAddEquipmentDialog(true);
                    }}
                >
                    {`${t("Gui.Buttons.Add")} ${t("Entities.EquipmentType.EquipmentType")}`}
                </Button>
            </div>

            <div className="scrollable-list card card--nested card--alt">
                <div className="scrollable-list__scroller scrollable-list__scroller--large">
                    <div className="entity-list">
                        {loadingEquipmentTypes ? (
                            <div className="entity-list__item entity-list__item--loading">
                                <CircularProgress />
                            </div>
                        ) : null}
                        {equipmentTypes?.data?.filter(filterFunc).map((item, index) => (
                            <div className="entity-list__item" key={index}>
                                <a
                                    className="entity-list__item-link entity-list__item-link--padding"
                                    href={`#!/equipmentTypes/${item.id}`}
                                >
                                    <div className="entity-list__item-icon entity-list__item-icon--alt">
                                        <i className={`fal fa-${item.icon}`} />
                                    </div>
                                    <div className="entity-list__item-panel">
                                        <p className="paragraph paragraph--no-spacing">{item.displayName}</p>
                                        <p className="caption caption--no-spacingp">{item.className}</p>
                                    </div>
                                    <div className="entity-list__item-arrow">
                                        <FontAwesomeIcon icon={faChevronRight} />
                                    </div>
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Dialog open={showAddEquipmentDialog}>
                <AddEquipmentType
                    irManufacturersRepository={props.irManufacturersRepository}
                    mmEquipmentTypeRepository={props.mmEquipmentTypeRepository}
                    onClose={() => {
                        setShowAddEquipmentDialog(false);
                    }}
                    onAddSuccess={addEquipmentType}
                />
            </Dialog>
        </div>
    );
};
